import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, apiErrorText } from '../lib/api';
import { timeOpts } from '../lib/timePrefs.js';
import { filePreviewKind, safePreviewBlob } from '../lib/filePreview.js';
import {
  sortRooms,
  startedRoomId,
  subjectProblem,
  SUBJECT_MAX,
  threadLabel,
  toChatMsgs,
  withNewRoom,
  type ChatMsg,
  type MxContent,
  type MxEvent,
  type PortalRoom,
} from '../lib/portalChat';

/**
 * Embedded secure chat for the contact drawer. ehr#536 (ruling 2026-10-21): it shows and sends in the
 * client's PORTAL conversations (the ones the client sees in their portal), with a thread picker and
 * "New thread". Staff-only talk about the client stays in the Messaging app and never shows here.
 * The EHR API brokers the Matrix session and the room list (POST /contacts/:id/chat/session, see
 * apps/api chat module); this component then talks to Synapse directly (CORS-open) with the returned
 * token: history via /messages, send via /send, media via /media, polling every 8s. Nothing is
 * persisted client-side beyond the in-memory session.
 */

interface ChatSession {
  baseUrl: string;
  accessToken: string;
  mxid: string;
  expiresAt: number;
  portalReady: boolean;
  rooms: PortalRoom[];
}
interface NewThread {
  roomId: string;
  name: string | null;
  subject: string | null;
  channel: string;
}

const POLL_MS = 8000;
const HISTORY = 60;

const whenText = (ms: number) =>
  new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', ...timeOpts() });

export function MessagesPanel({
  contactId,
  contactName,
}: {
  contactId: string;
  contactName: string;
}) {
  const who = contactName || 'This client';
  const [status, setStatus] = useState<'loading' | 'ready' | 'noportal' | 'forbidden' | 'error'>(
    'loading',
  );
  const [errorText, setErrorText] = useState('');
  const [rooms, setRooms] = useState<PortalRoom[]>([]);
  const [roomId, setRoomId] = useState<string | null>(null);
  const [roomStatus, setRoomStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [roomError, setRoomError] = useState('');
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // New thread
  const [newOpen, setNewOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [newError, setNewError] = useState('');
  const [creating, setCreating] = useState(false);

  const sessionRef = useRef<ChatSession | null>(null);
  const roomsRef = useRef<PortalRoom[]>([]);
  const activeRoom = useRef<string | null>(null);
  const objectUrls = useRef<string[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);

  const openSession = useCallback(async (): Promise<ChatSession> => {
    const s = await api.post<ChatSession>(`/contacts/${contactId}/chat/session`);
    sessionRef.current = s;
    return s;
  }, [contactId]);

  // Synapse CS-API wrapper: Bearer auth, one silent re-mint on 401.
  const mx = useCallback(
    async (path: string, init?: RequestInit, retried = false): Promise<Response> => {
      let s = sessionRef.current;
      if (!s || Date.now() > s.expiresAt - 60_000) s = await openSession();
      const res = await fetch(s.baseUrl + path, {
        ...init,
        headers: { Authorization: `Bearer ${s.accessToken}`, ...(init?.headers ?? {}) },
      });
      if (res.status === 401 && !retried) {
        await openSession();
        return mx(path, init, true);
      }
      return res;
    },
    [openSession],
  );

  const resolveMedia = useCallback(
    async (mxc: string, name: string): Promise<string | null> => {
      const id = mxc.replace('mxc://', '');
      let res = await mx(`/_matrix/client/v1/media/download/${id}`);
      if (!res.ok) res = await mx(`/_matrix/media/v3/download/${id}`);
      if (!res.ok) return null;
      // sec 2026-10-24: the uploader picks the type; an HTML or SVG blob would run in the EHR's origin.
      const url = URL.createObjectURL(safePreviewBlob(await res.blob(), name));
      objectUrls.current.push(url);
      setMediaUrls((prev) => (prev[mxc] ? prev : { ...prev, [mxc]: url }));
      return url;
    },
    [mx],
  );

  // Make the signed-in staff member a joined member of the conversation. Not in it at all → the EHR
  // API asks Messaging to invite them; then the invite is accepted on Synapse (Synapse answers 403
  // M_FORBIDDEN on /messages and /send until membership is `join`). Idempotent once joined.
  const ensureJoined = useCallback(
    async (rid: string): Promise<void> => {
      const room = roomsRef.current.find((r) => r.roomId === rid);
      if (room?.membership === 'join') return;
      if (!room?.membership) {
        await api.post(`/contacts/${contactId}/chat/rooms/${encodeURIComponent(rid)}/join`);
      }
      const res = await mx(`/_matrix/client/v3/rooms/${encodeURIComponent(rid)}/join`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) throw new Error(`join ${res.status}`);
      setRooms((prev) =>
        prev.map((r) => (r.roomId === rid ? { ...r, membership: 'join' as const } : r)),
      );
    },
    [contactId, mx],
  );

  const loadHistory = useCallback(
    async (rid: string): Promise<void> => {
      const s = sessionRef.current;
      if (!s) return;
      const res = await mx(
        `/_matrix/client/v3/rooms/${encodeURIComponent(rid)}/messages?dir=b&limit=${HISTORY}`,
      );
      if (!res.ok) throw new Error(`history ${res.status}`);
      const data = (await res.json()) as { chunk?: MxEvent[] };
      if (activeRoom.current !== rid) return; // switched threads meanwhile
      const mapped = toChatMsgs(data.chunk, s.mxid);
      setMsgs(mapped);
      // Lazily resolve image previews.
      for (const m of mapped) {
        if (m.att?.kind === 'image') void resolveMedia(m.att.mxc, m.att.name);
      }
    },
    [mx, resolveMedia],
  );

  // Boot: the session and the client's portal conversations; the most recent one opens first.
  useEffect(() => {
    let alive = true;
    const urls = objectUrls.current;
    (async () => {
      try {
        const s = await openSession();
        if (!alive) return;
        if (!s.portalReady) {
          setStatus('noportal');
          return;
        }
        const list = sortRooms(s.rooms ?? []);
        roomsRef.current = list;
        setRooms(list);
        setRoomId(list[0]?.roomId ?? null);
        setStatus('ready');
      } catch (e) {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 403) {
          setErrorText(
            e.code === 'PHI_FORBIDDEN' ? 'Your role cannot access secure messaging.' : e.message,
          );
          setStatus('forbidden');
        } else setStatus('error');
      }
    })();
    return () => {
      alive = false;
      urls.forEach((u) => URL.revokeObjectURL(u));
      objectUrls.current = [];
    };
  }, [openSession]);

  // The open conversation: join if needed, load, then poll every 8s.
  useEffect(() => {
    activeRoom.current = roomId;
    if (!roomId) return;
    let alive = true;
    let timer: ReturnType<typeof setInterval> | null = null;
    (async () => {
      setMsgs([]);
      setRoomStatus('loading');
      setRoomError('');
      try {
        await ensureJoined(roomId);
        await loadHistory(roomId);
        if (!alive) return;
        setRoomStatus('ready');
        timer = setInterval(() => void loadHistory(roomId).catch(() => {}), POLL_MS);
      } catch (e) {
        if (!alive) return;
        setRoomError(
          e instanceof ApiError && e.status !== 502 && e.status !== 0
            ? e.message
            : "This conversation couldn't be opened right now. Try again shortly.",
        );
        setRoomStatus('error');
      }
    })();
    return () => {
      alive = false;
      if (timer) clearInterval(timer);
    };
  }, [roomId, ensureJoined, loadHistory]);

  // Autoscroll to newest.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, status, roomStatus]);

  const send = useCallback(
    async (content: MxContent): Promise<void> => {
      const rid = activeRoom.current;
      if (!sessionRef.current || !rid) return;
      const txn = `${Date.now()}${Math.random().toString(36).slice(2)}`;
      const res = await mx(
        `/_matrix/client/v3/rooms/${encodeURIComponent(rid)}/send/m.room.message/${txn}`,
        {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(content),
        },
      );
      if (!res.ok) throw new Error(`send ${res.status}`);
      setRooms((prev) => prev.map((r) => (r.roomId === rid ? { ...r, lastTs: Date.now() } : r)));
    },
    [mx],
  );

  const onSendText = useCallback(async () => {
    const body = draft.trim();
    const rid = activeRoom.current;
    if (!body || sending || !rid) return;
    setSending(true);
    try {
      await send({ msgtype: 'm.text', body });
      setDraft('');
      await loadHistory(rid);
    } catch {
      /* keep the draft so the user can retry */
    } finally {
      setSending(false);
    }
  }, [draft, sending, send, loadHistory]);

  const onPickFile = useCallback(
    async (file: File) => {
      const rid = activeRoom.current;
      if (sending || !rid) return;
      setSending(true);
      try {
        const up = await mx(`/_matrix/media/v3/upload?filename=${encodeURIComponent(file.name)}`, {
          method: 'POST',
          headers: { 'content-type': file.type || 'application/octet-stream' },
          body: file,
        });
        const j = (await up.json()) as { content_uri?: string };
        if (j.content_uri) {
          await send({
            msgtype: file.type.startsWith('image/') ? 'm.image' : 'm.file',
            body: file.name,
            url: j.content_uri,
          });
          await loadHistory(rid);
        }
      } catch {
        /* ignore: surfaced by the next poll if it partially went through */
      } finally {
        setSending(false);
        if (fileRef.current) fileRef.current.value = '';
      }
    },
    [mx, sending, send, loadHistory],
  );

  const openFileAttachment = useCallback(
    async (mxc: string, name: string) => {
      const url = mediaUrls[mxc] ?? (await resolveMedia(mxc, name));
      if (!url) return;
      // A picture, PDF or text file opens in a tab; anything else (Word, a zip, an HTML file) downloads.
      if (filePreviewKind(name) !== 'none') {
        window.open(url, '_blank', 'noopener');
        return;
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
    },
    [mediaUrls, resolveMedia],
  );

  const onStartThread = useCallback(async () => {
    const problem = subjectProblem(subject);
    if (problem) {
      setNewError(problem);
      return;
    }
    if (creating) return;
    setCreating(true);
    setNewError('');
    try {
      const t = await api.post<NewThread>(`/contacts/${contactId}/chat/thread`, {
        subject: subject.replace(/\s+/g, ' ').trim(),
      });
      // The server joins the caller before it answers.
      const room: PortalRoom = { ...t, lastTs: Date.now(), membership: 'join' };
      roomsRef.current = withNewRoom(roomsRef.current, room);
      setRooms((prev) => withNewRoom(prev, room));
      setRoomId(t.roomId);
      setSubject('');
      setNewOpen(false);
    } catch (e) {
      // messaging#62: made, but the server couldn't add me yet → open it; opening joins it (/join)
      const made = e instanceof ApiError ? startedRoomId(e.code, e.details) : null;
      if (made) {
        const clean = subject.replace(/\s+/g, ' ').trim();
        const room: PortalRoom = {
          roomId: made,
          name: null,
          subject: clean,
          channel: 'careteam',
          lastTs: Date.now(),
          membership: null,
        };
        roomsRef.current = withNewRoom(roomsRef.current, room);
        setRooms((prev) => withNewRoom(prev, room));
        setRoomId(made);
        setSubject('');
        setNewOpen(false);
      } else {
        setNewError(apiErrorText(e, "The thread couldn't be started. Try again shortly."));
      }
    } finally {
      setCreating(false);
    }
  }, [subject, creating, contactId]);

  const empty = useMemo(
    () => roomStatus === 'ready' && msgs.length === 0,
    [roomStatus, msgs.length],
  );

  if (status === 'loading') return <p className="muted">Connecting to secure chat…</p>;
  if (status === 'forbidden') return <p className="muted">{errorText}</p>;
  if (status === 'error')
    return <p className="muted">Secure chat is unavailable right now. Try again shortly.</p>;
  if (status === 'noportal')
    return (
      <p className="muted">
        {who} hasn&apos;t set up their portal yet, so there&apos;s no conversation with them here.
      </p>
    );

  return (
    <div className="chat-panel">
      <div className="chat-threads">
        {rooms.length > 0 ? (
          <select
            className="chat-thread-pick"
            aria-label="Conversation"
            value={roomId ?? ''}
            onChange={(e) => setRoomId(e.target.value || null)}
          >
            {rooms.map((r) => (
              <option key={r.roomId} value={r.roomId}>
                {threadLabel(r)}
                {r.lastTs ? ` · ${whenText(r.lastTs)}` : ''}
              </option>
            ))}
          </select>
        ) : (
          <span className="muted chat-thread-none">No conversations yet</span>
        )}
        {!newOpen && (
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              setNewOpen(true);
              setNewError('');
            }}
          >
            New thread
          </button>
        )}
      </div>

      {newOpen && (
        <form
          className="chat-new"
          onSubmit={(e) => {
            e.preventDefault();
            void onStartThread();
          }}
        >
          <input
            className="chat-new-subject"
            aria-label="Subject"
            placeholder="Subject"
            maxLength={SUBJECT_MAX}
            value={subject}
            autoFocus
            onChange={(e) => {
              setSubject(e.target.value);
              if (newError) setNewError('');
            }}
          />
          <button type="submit" className="btn sm primary" disabled={creating}>
            {creating ? 'Starting…' : 'Start'}
          </button>
          <button
            type="button"
            className="btn sm"
            disabled={creating}
            onClick={() => {
              setNewOpen(false);
              setSubject('');
              setNewError('');
            }}
          >
            Cancel
          </button>
          <p className={newError ? 'chat-new-note err' : 'chat-new-note muted'}>
            {newError || `${who} will see this conversation in their portal.`}
          </p>
        </form>
      )}

      <div className="chat-scroll" ref={scrollRef}>
        {!roomId && (
          <p className="muted chat-empty">
            No conversations with {contactName || 'this client'} yet. Start one with New thread.
          </p>
        )}
        {roomId && roomStatus === 'loading' && msgs.length === 0 && (
          <p className="muted chat-empty">Loading…</p>
        )}
        {roomId && roomStatus === 'error' && <p className="muted chat-empty">{roomError}</p>}
        {empty && (
          <p className="muted chat-empty">
            No messages yet. {who} will see what you write here in their portal.
          </p>
        )}
        {roomStatus !== 'error' &&
          msgs.map((m) => (
            <div key={m.id} className={`chat-row ${m.from}`}>
              <div className="chat-bubble">
                {m.from !== 'mine' && (
                  <span className="chat-who">
                    {m.from === 'client' ? contactName || 'Client' : 'Staff'}
                  </span>
                )}
                {m.att?.kind === 'image' ? (
                  mediaUrls[m.att.mxc] ? (
                    <img className="chat-img" src={mediaUrls[m.att.mxc]} alt={m.att.name} />
                  ) : (
                    <span className="chat-att-loading">Loading image…</span>
                  )
                ) : m.att?.kind === 'file' ? (
                  <button
                    type="button"
                    className="chat-file"
                    onClick={() => void openFileAttachment(m.att!.mxc, m.att!.name)}
                  >
                    📎 {m.att.name}
                  </button>
                ) : (
                  <span className="chat-text">{m.text}</span>
                )}
                {m.at > 0 && <span className="chat-time">{whenText(m.at)}</span>}
              </div>
            </div>
          ))}
      </div>

      {roomId && roomStatus === 'ready' && (
        <div className="chat-composer">
          <input
            ref={fileRef}
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onPickFile(f);
            }}
          />
          <button
            type="button"
            className="chat-attach"
            title="Attach a file"
            disabled={sending}
            onClick={() => fileRef.current?.click()}
          >
            📎
          </button>
          <textarea
            className="chat-input"
            placeholder="Write a message…"
            value={draft}
            rows={1}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void onSendText();
              }
            }}
          />
          <button
            type="button"
            className="chat-send"
            disabled={sending || !draft.trim()}
            onClick={() => void onSendText()}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
